import { isObject, sortedUniqBy, entries } from "lodash";
import Vue, { PropType } from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";

import {
	HierarchyPartRepresentation,
	HierarchyRepresentation,
	HierarchySuggestionsRepresentation,
	StringSuggestionsRepresentation
} from "@/lib/apis/replicable/models";

import { Select as NekoSelect, Input as NekoInput } from "@/components/form";

@Component({
	components: {
		NekoInput,
		NekoSelect
	}
})
export default class SuggestionSelectComponent extends Vue {
	@Prop({
		default: null,
		type: Object as PropType<StringSuggestionsRepresentation | HierarchySuggestionsRepresentation>
	})
	public readonly suggestions!: Suggestions;

	@Prop({ default: false })
	public readonly showKey!: boolean;

	@Prop()
	public readonly value!: string;

	@Prop({ default: false })
	public readonly loading!: boolean;

	public valuePart: { [key: string]: string } = {};

	public internValue: string | null = null;
	public suggestionSelected: string | null = null;

	public get suggestionsSorted(): string[] {
		if (this.isStringSuggestions(this.suggestions)) {
			return sortedUniqBy(Object.keys(this.suggestions), key => this.suggestions[key]);
		} else {
			return sortedUniqBy(Object.keys(this.suggestions), key => this.$options.filters?.suggestion(this.suggestions[key]));
		}
	}

	public isHierarchySuggestions(suggestions: Suggestions): suggestions is HierarchySuggestionsRepresentation {
		for (const key in suggestions) {
			if (!suggestions.hasOwnProperty(key)) {
				continue;
			}

			if ("object" !== typeof suggestions[key]) {
				return false;
			}
		}

		return true;
	}

	public isLooseHierarchy(hierarchy: HierarchyRepresentation): boolean {
		for (const key in hierarchy) {
			if (!hierarchy.hasOwnProperty(key)) {
				continue;
			}

			if (!this.isLoosePart(hierarchy[key])) {
				return false;
			}
		}

		return true;
	}

	public isLoosePart(part: HierarchyPartRepresentation): boolean {
		return part.min != null || part.max != null;
	}

	public isStringSuggestions(suggestions: Suggestions): suggestions is StringSuggestionsRepresentation {
		for (const key in suggestions) {
			if (!suggestions.hasOwnProperty(key)) {
				continue;
			}

			if ("string" !== typeof suggestions[key]) {
				return false;
			}
		}

		return true;
	}

	public setValuePart(code: string, value: any) {
		this.$set(this.valuePart, code, value);

		this.internValue = entries(this.valuePart)
			.map(entry => `${entry[0]}=${entry[1] || ""}`)
			.join(";");

		this.$emit("input", this.internValue);
	}

	public validateValue() {
		if (this.internValue == null) {
			return;
		}

		for (const code in this.valuePart) {
			if (!this.valuePart[code] || this.valuePart[code].length === 0) {
				return;
			}
		}

		this.$emit("input", this.internValue);
	}

	public haveAllPartsWithValue() {
		if (!this.suggestionSelected) {
			return false;
		}

		if (isObject(this.suggestions[this.suggestionSelected])) {
			for (const partName in this.suggestions[this.suggestionSelected] as HierarchySuggestionsRepresentation) {
				if (!this.valuePart[partName]) {
					return false;
				}
			}
		}

		return true;
	}

	public getInputType(suggestionPart: HierarchyPartRepresentation) {
		const hasMax = suggestionPart.max;
		const hasMin = suggestionPart.min;
		const hasNumberValue = /^(?:-|\+)?\d+(?:\.\d+)?$/.test(suggestionPart.value);

		if (hasMax || hasMin || hasNumberValue) {
			return "number";
		}

		return "text";
	}

	private updateValuePart() {
		this.valuePart = {};

		if (this.internValue?.search(/.*[;=].*/) !== -1) {
			this.internValue?.split(";").forEach(part => {
				const keyValue = part.split("=");

				this.setValuePart(keyValue[0], keyValue[1]);
			});
		}
	}

	private created() {
		this.watchValue();
	}

	@Watch("value")
	private watchValue() {
		this.internValue = this.value;
		this.updateValuePart();
		this.updateSuggestionSelected();
	}

	@Watch("suggestions")
	private watchSuggestions(): void {
		this.updateSuggestionSelected();
	}

	private updateSuggestionSelected(): void {
		if (!this.value) {
			this.suggestionSelected = null;
			return;
		}

		if (this.isStringSuggestions(this.suggestions)) {
			this.suggestionSelected = this.value;
			return;
		}

		for (const key in this.suggestions) {
			// Si la valeur est identique à la clé de la suggestion
			// alors cette suggestion est sélectionnée
			if (this.value === key) {
				this.suggestionSelected = key;
				return;
			}

			const suggestion = this.suggestions[key] as HierarchyRepresentation

			// Si toutes les parties de la valeurs respectent les contraintes de la suggestion
			// alors cette suggestion est sélectionnée
			if (this.valueMatchesSuggestion(suggestion)) {
				this.suggestionSelected = key;
				return;
			}
		}

		this.suggestionSelected = null;
	}

	/**
	 * Vérifie si toutes {@link valuePart les parties de la valeur} respectent les contraintes d'une suggestion donnée.
	 * @param suggestion La suggestion
	 * @returns `true` si toutes les partie de la valeur respectent les contraintes de la suggestion, sinon `false`
	 */
	private valueMatchesSuggestion(suggestion: HierarchyRepresentation): boolean {
		for (const partKey in this.valuePart) {
			const constraints = suggestion[partKey] as HierarchyPartRepresentation | undefined;

			// Si la valeur ne respecte pas les contraintes
			// alors il n'est pas nécessaire de continuer
			if (!constraints || !this.matchesConstraints(this.valuePart[partKey], constraints)) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Vérifie si une valeur respecte des contraintes données.
	 * @param value La valeur
	 * @param constraints Les contraintes
	 * @returns `true` si la valeur respecte les contraintes données, sinon `false`
	 */
	private matchesConstraints(value: string, constraints: HierarchyPartRepresentation): boolean {
		if (constraints.value != null) {
			return constraints.value == value;
		} else if (constraints.min != null && constraints.max != null) {
			return constraints.min <= value && constraints.max >= value;
		} else if (constraints.min != null) {
			return constraints.min <= value;
		} else if (constraints.max != null) {
			return constraints.max >= value;
		} else {
			return false;
		}
	}

	@Watch("suggestionSelected")
	private watchSuggestionSelected(suggestionSelected: string | null, oldSuggestionSelected: string | null) {
		this.internValue = suggestionSelected;

		this.updateValuePart();

		if (this.haveAllPartsWithValue()) {
			this.validateValue();
		}
	}
}

type Suggestions = StringSuggestionsRepresentation | HierarchySuggestionsRepresentation;
